import { useFormikContext } from 'formik';
import { FC, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from "react-toastify";
import { WS_URL } from "../../constants/ws-constants";
import { useLocale } from '../../contexts/localeContext';
import { useFilter } from '../../contexts/table/FilterContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import closeIcon from '../../images/close.svg';
import { EnrolmentDevice } from '../../models/EnrolmentDevices';
import { MultiSelectOption } from '../../models/MultiSelectOption';
import { Name } from '../../models/Name';
import WSPluginService from '../../services/wsPluginService/wsPluginService';
import Label from '../label/label';
import SettingSelectField from '../settingFields/SettingSelectField';

interface TagSearchModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    availablePlugins: Name[];
}

export const TagSearchModal: FC<TagSearchModalProps> = ({ isOpen, setIsOpen, availablePlugins }) => {
    const { values } = useFormikContext<any>();
    const { translate } = useLocale();
    const enrolmentDevice = new EnrolmentDevice(translate);
    const [error, setError] = useState<string>("");
    const filterContext = useFilter();
    const [websocket, setWebsocket] = useState<WebSocket>();

    useEffect(() => {
        return (() => {
            websocket?.close();
        })
    }, [websocket])

    const addTagFilter = (tagCode: string) => {
        const newSelectedOption: MultiSelectOption = { value: tagCode, label: tagCode };
        const key = "tags.tagCodeUntruncated";
        const label = translate.byKey(TranslationKeys.cardNumber);

        filterContext.updateNonColumnFilter(newSelectedOption, key, label, true).then(() => {
            setIsOpen(false);
            websocket?.close();
        }).catch(() => {
            toast.warn(translate.byKey(TranslationKeys.failedToApplyFilter));
        })
    }

    const decodeMDEMessage = (data) => {
        const card = data ? data.split(";") : undefined;

        if (card) {
            const lowCard = card[0].split(":")[2];
            const highCard = card[1].split(":")[2]
            const lowCardNumber = lowCard.substring(lowCard.indexOf("=") + 1).toString();
            const highCardNumber = highCard.substring(highCard.indexOf("=") + 1).toString();

            if (highCardNumber === "0" && lowCardNumber === "0") {
                setError(translate.byKey(TranslationKeys.readingCardError))
            }
            else if (lowCardNumber === "0" && highCardNumber !== "0") {
                addTagFilter(highCardNumber);
            }
            else if (lowCardNumber !== "0" && highCardNumber === "0") {
                addTagFilter(lowCardNumber);

            }
            else if (lowCardNumber !== "0" && highCardNumber !== "0") {
                //TODO: allow a user to decide which which card number to use since this is a dual card.
                addTagFilter(lowCardNumber);
            }
        }
    }

    const decodeOmnikeyMessage = (data) => {
        if (data) {
            const cardInfo: string[] = data.split(":")
            const cardNumber = cardInfo[0].substring(cardInfo[0].indexOf("=") + 1);

            if (cardNumber === '0') {
                setError(translate.byKey(TranslationKeys.readingCardError))
            }
            else {
                addTagFilter(cardNumber);
            }
        }
    }
    const onMessage = (data) => {
        const response = JSON.parse(data);

        if (response.enrollType === enrolmentDevice.MDE.type) {
            decodeMDEMessage(response.enrollData)
        }
        else if (response.enrollType === enrolmentDevice.OMNIKEY.type) {
            decodeOmnikeyMessage(response.enrollData)
        }
    }

    const onWebsocketClose = () => {
        websocket?.close();
    }

    const onError = (error) => {
        setError(translate.byKey(TranslationKeys.pluginError));
    }

    const onOpen = (webSocket: WebSocket, value) => {
        setWebsocket(webSocket)
    }

    const onScan = () => {
        const mdeDevice = enrolmentDevice.MDE;
        const omnikeyDevice = enrolmentDevice.OMNIKEY;
        setError("");
        switch (values.pluginId) {
            case mdeDevice.type:
                websocket?.send(mdeDevice.command ?? '');
                break;
            case omnikeyDevice.type:
                websocket?.send(omnikeyDevice.command ?? '')
                break;
        }
    }

    return <>
        <WSPluginService
            onMessage={onMessage}
            onClosed={onWebsocketClose}
            onError={onError}
            onOpen={onOpen}
            url={window.location.protocol === "https:" ? WS_URL.SECURED_PLUGIN_MANAGER_WEBSOCKET : WS_URL.UNSECURED_PLUGIN_MANAGER_WEBSOCKET} />
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
            className="bg-white border-none m-auto shadow select-none outline-none w-content">
            <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                <div className="flex flex-row">
                    <p className="pl-1 font-bold font text-white">{translate.byKey(TranslationKeys.searchEnrolmentByCard)}</p>
                </div>
                <img src={closeIcon} onClick={(e) => setIsOpen(false)} className="cursor-pointer mr-2" />
            </div>
            <>
                <div className="pt-3 px-3" style={{ width: '250px' }} >
                    <Label
                        required
                        htmlFor="name"
                        className="text-xs text-gray-600 mt-1">
                        {translate.byKey(TranslationKeys.enrolmentDevice)}
                    </Label>
                    <SettingSelectField
                        accessor="pluginId"
                        options={availablePlugins}
                        className="w-56" />
                    <span hidden={false} className="text-red-600 text-xs">{error}</span>
                </div>
                <div className="flex flex-row pb-3 pr-3 justify-end bg-white">
                    <div className="flex flex-row mt-4">
                        <button
                            id='submitBtn'
                            className={`secondary py-1 px-5 ${values.pluginId ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                            type='button'
                            onClick={onScan}
                            disabled={values.pluginId ? false : true}>
                            {translate.byKey(TranslationKeys.scan)}
                        </button>
                    </div>
                </div>
            </>
        </Modal>
    </>
}

